import AvatarIcon from '../../images/icons/avatar.svg?react';
import React from 'react';
import {Member, useAppContext} from '../../app-context';
import {getInitials} from '../../utils/helpers';

type LikeAvatarProps = {
    member?: Member;
};

function getDimensionClasses() {
    return 'w-7 h-7 sm:w-5 sm:h-5';
}

export const LikeAvatar: React.FC<LikeAvatarProps> = ({member}) => {
    const {avatarSaturation} = useAppContext();
    const dimensionClasses = getDimensionClasses();

    // 名前が無い場合は 'Anonymous' としてハッシュ生成
    const memberName = member?.name || 'Anonymous';

    // ハッシュ値の生成 & 彩度や明度を算出する関数
    const getHashOfString = (str: string) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        return Math.abs(hash);
    };

    const normalizeHash = (hash: number, min: number, max: number) => {
        return Math.floor((hash % (max - min)) + min);
    };

    // 名前をもとに適当なHSLカラーを作る
    const generateHSL = (): [number, number, number] => {
        const hash = getHashOfString(memberName);
        const saturation = avatarSaturation === undefined || isNaN(avatarSaturation) ? 50 : avatarSaturation;

        const hRange = [0, 360];
        const lRangeTop = Math.round(saturation / (100 / 30)) + 30;
        const lRangeBottom = lRangeTop - 20;
        const lRange = [lRangeBottom, lRangeTop];

        const h = normalizeHash(hash, hRange[0], hRange[1]);
        const l = normalizeHash(hash, lRange[0], lRange[1]);

        return [h, saturation, l];
    };

    // HSL色をCSS用文字列に変換
    const HSLtoString = ([h, s, l]: [number, number, number]) => {
        return `hsl(${h}, ${s}%, ${l}%)`;
    };

    // イニシャルを生成 (名前が無い場合は Anonymous を想定)
    const getMemberInitials = () => {
        return getInitials(memberName);
    };

    // 背景色
    const bgColor = HSLtoString(generateHSL());
    const avatarStyle = {background: bgColor};

    // 実際の描画要素
    return (
        <figure className={`relative ${dimensionClasses}`}>
            {memberName ? (
                <div
                    className={`flex items-center justify-center rounded-full ${dimensionClasses}`}
                    data-testid="avatar-background"
                    style={avatarStyle}
                >
                    <p className="font-sans text-sm font-semibold text-white">
                        { getMemberInitials() }
                    </p>
                </div>
            ) : (
                // 名前すら無い場合 (例えばDeletedメンバーなど)
                <div
                    className={`flex items-center justify-center rounded-full bg-neutral-900 dark:bg-[rgba(255,255,255,0.7)] ${dimensionClasses}`}
                    data-testid="avatar-background"
                >
                    <AvatarIcon className="stroke-white dark:stroke-[rgba(0,0,0,0.6)]" />
                </div>
            )}
            {/* avatar_image があれば、最前面に重ねる */}
            {member?.avatar_image && (
                <img
                    alt="Avatar"
                    className={`absolute left-0 top-0 rounded-full ${dimensionClasses}`}
                    src={member.avatar_image}
                />
            )}
        </figure>
    );
};